import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const locale = "sr";

export const employeesManagement = createApi({
    reducerPath: "employeesManagement",
    baseQuery: fetchBaseQuery({
        baseUrl: `${BASE_URL}`,
        prepareHeaders: (headers) => {
            const jwtToken = localStorage.getItem("userToken") || sessionStorage.getItem("userToken");

            if (jwtToken) {
                headers.set("authorization", `Bearer ${jwtToken}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        employeesList: builder.mutation({
            query: (body) => {
                let url = `v1/${locale}/employees`;

                for (const property in body) {
                    if ((property && body[property]) || (property === "startPage" && body[property] === 0)) {
                        if (url?.includes("?")) {
                            url += `&${property}=${body[property]}`;
                        } else {
                            url += `?${property}=${body[property]}`;
                        }
                    }
                }

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        viewEmployee: builder.mutation({
            query: (employeeId) => ({
                url: `v1/${locale}/employees/${employeeId}`,
                method: "GET",
            }),
        }),
        addEmployee: builder.mutation({
            query: (body) => ({
                url: `v1/${locale}/employees`,
                method: "POST",
                body: body,
            }),
        }),
        updateEmployee: builder.mutation({
            query: ({ body, employeeId }) => ({
                url: `v1/${locale}/employees/${employeeId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteEmployee: builder.mutation({
            query: (employeeId) => ({
                url: `v1/${locale}/employees/${employeeId}`,
                method: "DELETE",
            }),
        }),
        listDocuments: builder.mutation({
            query: (employeeId) => ({
                url: `v1/${locale}/employees/${employeeId}/documents`,
                method: "GET",
            }),
        }),
        addDocument: builder.mutation({
            query: ({ file, employeeId }) => {
                let formData = new FormData();
                formData.append("file", file);

                return {
                    url: `v1/${locale}/employees/${employeeId}/documents`,
                    method: "POST",
                    body: formData,
                };
            },
        }),
        viewDocument: builder.mutation({
            query: ({ employeeId, documentId }) => ({
                url: `v1/${locale}/employees/${employeeId}/documents/${documentId}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
        deleteDocument: builder.mutation({
            query: ({ employeeId, documentId }) => ({
                url: `v1/${locale}/employees/${employeeId}/documents/${documentId}`,
                method: "DELETE",
            }),
        }),
        notesList: builder.mutation({
            query: (employeeId) => ({
                url: `v1/${locale}/employees/${employeeId}/notes`,
                method: "GET",
            }),
        }),
        viewNote: builder.mutation({
            query: ({ employeeId, noteId }) => ({
                url: `v1/${locale}/employees/${employeeId}/notes/${noteId}`,
                method: "GET",
            }),
        }),
        addNote: builder.mutation({
            query: ({ body, employeeId }) => ({
                url: `v1/${locale}/employees/${employeeId}/notes`,
                method: "POST",
                body: body,
            }),
        }),
        deleteNote: builder.mutation({
            query: ({ employeeId, noteId }) => ({
                url: `v1/${locale}/employees/${employeeId}/notes/${noteId}`,
                method: "DELETE",
            }),
        }),
        addAvatar: builder.mutation({
            query: ({ file, employeeId }) => {
                let formData = new FormData();
                formData.append("file", file);

                return {
                    url: `v1/${locale}/employees/${employeeId}/avatar`,
                    method: "POST",
                    body: formData,
                };
            },
        }),
        viewAvatar: builder.mutation({
            query: (employeeId) => ({
                url: `v1/${locale}/employees/${employeeId}/avatar`,
                method: "GET",
            }),
        }),
        deleteAvatar: builder.mutation({
            query: (employeeId) => ({
                url: `v1/${locale}/employees/${employeeId}/avatar`,
                method: "DELETE",
            }),
        }),
        exportEmployees: builder.mutation({
            query: () => ({
                url: `v1/${locale}/export/employees`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
        canEmployeeArchive: builder.mutation({
            query: ({employeeId}) => ({
                url: `v1/${locale}/employees/${employeeId}/can-archive`,
                method: "GET",
            }),
        }),

        archiveEmployee: builder.mutation({
            query: ({ employeeId, body }) => ({
                url: `v1/${locale}/employees/${employeeId}/archive`,
                method: "PATCH",
                body: body
            }),
        }),
    }),
});

export const {
    useAddEmployeeMutation,
    useDeleteEmployeeMutation,
    useEmployeesListMutation,
    useUpdateEmployeeMutation,
    useViewEmployeeMutation,
    useListDocumentsMutation,
    useAddDocumentMutation,
    useDeleteDocumentMutation,
    useViewDocumentMutation,
    useAddNoteMutation,
    useViewNoteMutation,
    useNotesListMutation,
    useAddAvatarMutation,
    useViewAvatarMutation,
    useDeleteAvatarMutation,
    useExportEmployeesMutation,
    useArchiveEmployeeMutation,
    useCanEmployeeArchiveMutation
} = employeesManagement;
